<template>
	<div :data-theme="route.params.resort" class="step-reserve">
		<PageHeader
			:title="headerInfo.title"
			:notice="availabilityNotice"
			:image="headerInfo.image"
			:back-to="backTo"
			:class="{ 'min-h-67': headerInfo.image }"
		/>

		<DatePicker v-model="selectedDate" :workspace-id="workspaceId" />

		<div class="py-5 px-4.5 relative flex flex-col gap-3" aria-live="polite" :aria-busy="loading">
			<template v-if="loading">
				<p class="sr-only" role="status">{{ t('loading') }}</p>
				<SkeletonLoadingCard type="start-option" />
				<SkeletonLoadingCard type="start-option" />
			</template>

			<template v-if="!loading">
				<div v-for="option in visibleCards" :key="option.id">
					<!-- when there are more than 1 subcards, display all subcards -->
					<RouterLinkButton
						v-if="option.cards && option.cards.length > 1"
						:to="{
							name: 'resort-reserve',
							params: { date: selectedDate, card_id: option.id },
						}"
						class="flex items-center w-full gap-4 p-4 text-left bg-white rounded-lg shadow-md"
						:title="option.description"
					>
						<span
							v-if="option.icon"
							class="flex justify-center items-center text-neutral-900 size-12 flex-none rounded-lg bg-gold-100"
						>
							<SvgIcon :name="option.icon" class="h-4" />
						</span>

						<span class="flex flex-col flex-1 gap-1">
							<span class="inline-block font-serif font-bold">{{ option.name }}</span>
						</span>
					</RouterLinkButton>

					<!-- when there is exactly 1 subcard, we go directly to that productset -->
					<RouterLinkButton
						v-else-if="option.cards && option.cards.length === 1"
						:to="{
							name: 'resort-options',
							params: { date: selectedDate, productset_id: option.cards[0].product_set_id },
							query: parentCardQuery,
						}"
						class="flex items-center w-full gap-4 p-4 text-left bg-white rounded-lg shadow-md"
						:title="option.description"
					>
						<span
							v-if="option.icon"
							class="flex justify-center items-center text-neutral-900 size-12 flex-none rounded-lg bg-gold-100"
						>
							<SvgIcon :name="option.icon" class="h-4" />
						</span>
						<span class="flex flex-col flex-1 gap-1">
							<span class="inline-block font-serif font-bold">{{ option.name }}</span>
						</span>
					</RouterLinkButton>

					<!-- when a product_set_id is set, we go to that productset -->
					<RouterLinkButton
						v-else-if="option.product_set_id"
						:to="{
							name: 'resort-options',
							params: { date: selectedDate, productset_id: option.product_set_id },
							query: parentCardQuery,
						}"
						class="flex items-center w-full gap-4 p-4 text-left bg-white rounded-lg shadow-md"
						:title="option.description"
					>
						<span
							v-if="option.icon"
							class="flex justify-center items-center text-neutral-900 size-12 flex-none rounded-lg bg-gold-100"
						>
							<SvgIcon :name="option.icon" class="h-4" />
						</span>
						<span class="flex flex-col flex-1 gap-1">
							<span class="inline-block font-serif font-bold">{{ option.name }}</span>
						</span>
					</RouterLinkButton>

					<!-- else when the card has a url defined we open the url of the card -->
					<a
						v-else-if="option.url && option.id !== 'reserve-with-bath-card'"
						:href="option.url"
						target="_self"
						class="relative flex items-center gap-4 p-4 bg-white rounded-lg shadow-md"
						:title="option.description"
						@click.prevent="openUrl(option.url)"
					>
						<span
							v-if="option.icon"
							class="flex justify-center items-center text-neutral-900 size-12 flex-none rounded-lg bg-gold-100"
						>
							<SvgIcon :name="option.icon" class="h-4" />
						</span>

						<i class="h-4 w-4 text-neutral-500 absolute right-4 top-6 flex items-center justify-center">
							<svg xmlns="http://www.w3.org/2000/svg" height="13" width="13" viewBox="0 0 13.002 13.001">
								<path
									fill="currentColor"
									fill-rule="nonzero"
									d="M5.834 0a.5.5 0 1 1 0 1H1.01l-.01.008.001.105v10.879l.15.009H12.001l-.001-.15V7.168a.5.5 0 1 1 1 0v4.935c0 .076 0 .144-.002.172-.01.216-.055.376-.202.523-.147.147-.306.191-.523.202a2.906 2.906 0 0 1-.1.002H.9c-.076 0-.144 0-.172-.002-.216-.011-.376-.055-.523-.202-.147-.147-.19-.307-.202-.523L0 12.206V.844C0 .79 0 .748.002.727.013.51.057.35.204.204.351.057.511.013.727.002L.795 0h5.04Zm6.667 0h.021l.042.004L12.5 0a.502.502 0 0 1 .353.147l.013.013a.503.503 0 0 1 .032.038l-.045-.051a.502.502 0 0 1 .147.353v4a.5.5 0 0 1-1 0L12 1.708l-6.479 6.48a.5.5 0 1 1-.707-.708L11.294 1H8.501a.5.5 0 0 1-.496-.432L8.001.5a.5.5 0 0 1 .5-.5h4Z"
								/>
							</svg>
						</i>
						<span class="flex flex-col flex-1 gap-1">
							<span class="inline-block font-serif font-bold">{{ option.name }}</span>
						</span>
					</a>
				</div>
			</template>

			<button
				v-if="!loading && !rootCard"
				type="button"
				class="btn-transparent w-full text-sm"
				@click="revealVoucherPopup"
			>
				<VoucherIcon class="h-3 text-white flex-none" />
				{{ t('reserve_with_voucher') }}
			</button>

			<button
				v-if="!loading && !rootCard && giftVoucherExplanation"
				type="button"
				class="btn-transparent w-full text-sm"
				@click="showGiftVoucherPopup = true"
			>
				<GiftIcon class="h-4 text-white flex-none" />
				{{ t('i_have_a_gift_voucher') }}
			</button>

			<a
				v-if="!loading && reserveWithBathCard && reserveWithBathCard.url"
				class="btn-transparent w-full text-sm"
				:href="reserveWithBathCard.url"
				target="_self"
				@click.prevent="openUrl(reserveWithBathCard.url)"
			>
				<CardIcon class="h-3 text-white flex-none" />
				{{ t('reserve_with_bathcard') }}
			</a>
		</div>

		<VoucherModal v-if="showVoucherPopup" :workspace-id="workspaceId" @close="showVoucherPopup = false" />

		<GiftVoucherModal
			v-if="showGiftVoucherPopup"
			:explanation="giftVoucherExplanation"
			@close="showGiftVoucherPopup = false"
		/>
	</div>
</template>

<script lang="ts" setup>
import { onMounted, computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import bridge from '@thermenresorts/bridge';
import { useApi } from '../api';
import PageHeader from '../components/PageHeader.vue';
import useStore from '../store';
import RouterLinkButton from '../components/RouterLinkButton.vue';
import type {ReserveCard, ReserveEndpoint} from '../api/resources';
import VoucherModal from "../components/VoucherModal.vue";
import GiftVoucherModal from "../components/GiftVoucherModal.vue";
import DatePicker from "../components/DatePicker.vue";
import SkeletonLoadingCard from '../components/SkeletonLoadingCard.vue';
import CardIcon from '@thermenresorts/icons/svg/card.svg';
import VoucherIcon from '@thermenresorts/icons/svg/voucher.svg';
import GiftIcon from '@thermenresorts/icons/svg/gift.svg';
import SvgIcon from '../components/SvgIcon.vue';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const store = useStore();
const api = useApi();

// #4774: Guard against an invalid/mis-embedded resort ref that would otherwise crash render.
if (!store.hasResort(route.params.resort as string)) {
  router.replace({ name: 'resort-choice' });
}

const loading = ref(true);
const reserveData = ref<ReserveEndpoint['data']>();
const showVoucherPopup = ref(false);
const showGiftVoucherPopup = ref(false);
const selectedDate = ref<string>(route.params.date as string);

const giftVoucherExplanation = computed(() => reserveData.value?.gift_voucher_explanation ?? '');

watch(() => route.params.date, (newDate) => {
  selectedDate.value = newDate as string;
});

const openUrl = (url: string) => {
  bridge.sendMessage('pre-reservation-flow', 'redirect', { url });
};

onMounted(() => {
  getReservationOptions()
});

// Remember the sub level (e.g. workshops overview), so the options page can navigate back to it
const parentCardQuery = computed(() => (route.params.card_id ? { parent_card: route.params.card_id } : {}));

const rootCard = computed((): ReserveCard|undefined|null => {
	if (!route.params.card_id) {
		return null;
	}

	const cards = (reserveData.value?.cards ?? []);
	return cards.find(card => card.id === route.params.card_id);
});

const visibleCards = computed(() => rootCard.value
	? (rootCard.value.cards ?? [])
	: (reserveData.value?.cards ?? [])
);

const reserveWithBathCard = computed(() => visibleCards.value.find(card => card.id === 'reserve-with-bath-card'));

const backTo = computed(() => {
	if (route.params.card_id) {
		return {
			name: 'resort-reserve',
			params: { resort: route.params.resort, date: route.params.date },
		};
	}
	if (store.state.canChooseResort) {
		return { name: 'home' };
	}
	return null;
});

const workspaceId = computed(() => store.getResort(route.params.resort as string)?.id);

const revealVoucherPopup = () => showVoucherPopup.value = true;
const hideVoucherPopup = () => showVoucherPopup.value = false;

// #4889: store.state.arrivalDate is only ever filled from the website's search bar (via the
// bridge), so an equal selectedDate means the guest landed here with that date prefilled.
// Resetting or picking another day breaks the match and hides the notice again.
const availabilityNotice = computed(() => (
	!route.params.card_id && store.state.arrivalDate && selectedDate.value === store.state.arrivalDate
		? t('availability_notice')
		: ''
));

const headerInfo = computed(() => {
	// Sub level
  if (rootCard.value) {
    return {
      title: rootCard.value.name ?? '',
      text: rootCard.value.description ?? '',
      image: undefined
    };
  }

	// Root / Top level
  return {
    title: reserveData.value?.title ?? '',
    text: reserveData.value?.description ?? '',
    image: 'header.webp',
  };
});

const getReservationOptions = async () => {
  const resort = store.getResort(route.params.resort as string);
  if (!resort) {
    loading.value = false;
    return;
  }

  try {
    const response = await api.reserve.get(resort.id);
    reserveData.value = response.data.data;
  } catch (error) {
    console.error(error);
  }

  loading.value = false;
};

defineExpose({
  revealVoucherPopup,
  hideVoucherPopup,
});
</script>
